import {
    CgLabel,
    CgMetadata,
    CgNetwork,
    CommodityGroup,
    type CgLabelHierarchy,
    type CgMetadataHierarchy,
    type CgNetworkHierarchy,
} from './commodityGroup';
import { mapValues } from 'lodash';

export interface CgSource {
    type: 'global' | 'store' | 'self';
    fullId: string;
    depth: number;
}

interface ResolvedValue<T> {
    current?: { value: T; source: CgSource };
    inherited?: { value: T; source: CgSource };
}

type Resolved<T> = { [Key in keyof T]?: ResolvedValue<T[Key]> };

export type ResolvedMetadata = Resolved<CgMetadata>;
export type ResolvedLabels = Record<string, ResolvedValue<CgLabel>>;
export type ResolvedNetworks = Record<string, ResolvedValue<CgNetwork>>;

export interface CommodityGroupExtended extends CommodityGroup {
    resolvedMetadata?: ResolvedMetadata;
    resolvedLabels?: ResolvedLabels;
    resolvedNetworks?: ResolvedNetworks;
}

export function extendCommodityGroup(cg: CommodityGroup, storeGuid: string | undefined): CommodityGroupExtended {
    return {
        ...cg,
        resolvedMetadata: resolve(cg, storeGuid, cg.metadataHierarchy, (entry) => entry.metadata),
        resolvedLabels: resolve(cg, storeGuid, cg.labelHierarchy, (entry) => entry.labels),
        resolvedNetworks: resolve(cg, storeGuid, cg.classificationNetworkHierarchy, (entry) => entry.classificationNetworks),
    };
}

function resolve<T extends CgMetadataHierarchy | CgLabelHierarchy | CgNetworkHierarchy, S extends Record<string, unknown>>(
    cg: CommodityGroup,
    storeGuid: string | undefined,
    data: T[] | undefined | null,
    selector: (entry: T) => S,
) {
    return (
        data?.reduce<Resolved<S>>((resolved, entry) => {
            const isFromStore = 'source' in entry && entry.source === 'storeMetadataOverrides';

            let type: CgSource['type'];
            if (entry.fullId === cg.fullId && (!storeGuid || isFromStore)) {
                type = 'self';
            } else if (isFromStore) {
                type = 'store';
            } else {
                type = 'global';
            }

            const source = { type, fullId: entry.fullId, depth: entry.depth };

            return {
                ...resolved,
                ...mapValues(selector(entry), (value, key) => ({
                    current: { value, source },
                    inherited: source.type === 'self' ? resolved[key as keyof Resolved<S>]?.current : { value, source },
                })),
            };
        }, {}) ?? {}
    );
}
